import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Truck } from "lucide-react";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/shipping-policy")({
  head: () => ({
    meta: [
      { title: "Shipping Policy — Healic" },
      {
        name: "description",
        content:
          "Learn about Healic's shipping and delivery policy for pharmacy products, medical supplies, and home sample collection.",
      },
      { property: "og:title", content: "Shipping Policy — Healic" },
      {
        property: "og:description",
        content:
          "Shipping, delivery, and home collection policies for Healic products and services.",
      },
      { property: "og:url", content: `${SITE_URL}/shipping-policy` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/shipping-policy` }],
  }),
  component: ShippingPolicyPage,
});

const sections = [
  {
    id: "overview",
    title: "Overview",
    content: [
      "Healic offers convenient delivery and home collection services to ensure timely access to healthcare. This Shipping Policy outlines the terms for delivery of pharmacy products, medical supplies, and home diagnostic sample collection.",
    ],
  },
  {
    id: "pharmacy-delivery",
    title: "Pharmacy Product Delivery",
    content: [
      "Medicines and healthcare products ordered through Healic Pharmacy are delivered as follows:",
    ],
    bullets: [
      "Same-day delivery available for orders placed before 2 PM within serviceable areas",
      "Standard delivery within 24-48 hours for most locations",
      "Delivery areas currently include Gurugram, Agra, Haridwar, and Indirapuram (Ghaziabad)",
      "Orders are processed only after prescription verification (for prescription medicines)",
      "Delivery charges may apply based on order value and location; free delivery for orders above INR 500",
    ],
  },
  {
    id: "medical-supplies",
    title: "Medical Supplies and Devices",
    content: [
      "For medical equipment, surgical supplies, and devices:",
    ],
    bullets: [
      "Standard delivery: 2-5 business days depending on product availability",
      "Express delivery: 1-2 business days for in-stock items (additional charges may apply)",
      "Bulky or specialized equipment may require scheduled delivery with installation support",
      "Products are inspected and packaged securely before dispatch",
      "Temperature-sensitive items are shipped with appropriate cold chain packaging",
    ],
  },
  {
    id: "home-collection",
    title: "Home Sample Collection",
    content: [
      "Healic provides home sample collection for diagnostic tests:",
    ],
    bullets: [
      "Bookings can be made through the Healic Customer App or by calling our helpline",
      "Collection slots are available from 7 AM to 11 AM daily",
      "A trained phlebotomist or technician visits your home at the scheduled time",
      "Fasting requirements and pre-test instructions are communicated at the time of booking",
      "Reports are typically available within 24-48 hours and shared via the app and email",
    ],
  },
  {
    id: "delivery-process",
    title: "Delivery Process",
    content: [
      "How our delivery and collection process works:",
    ],
    bullets: [
      "Order confirmation with expected delivery/collection date and time slot",
      "SMS and app notifications at each stage: order confirmed, dispatched, out for delivery, delivered",
      "For home collection: technician details and ETA shared 30 minutes before arrival",
      "Deliveries require recipient verification via OTP or signature",
      "If recipient is unavailable, a re-attempt is made the next business day; maximum of 2 re-attempts",
    ],
  },
  {
    id: "shipping-charges",
    title: "Shipping Charges",
    content: [
      "Our shipping fee structure:",
    ],
    bullets: [
      "Pharmacy orders below INR 500: Standard delivery charge of INR 50",
      "Pharmacy orders above INR 500: Free delivery",
      "Diagnostics home collection: Free for test packages above INR 1,000; INR 100 for individual tests",
      "Express delivery: Additional INR 100-200 depending on location and urgency",
      "Remote or non-standard areas: Additional charges may apply and will be communicated before dispatch",
    ],
  },
  {
    id: "tracking",
    title: "Order Tracking",
    content: [
      "All orders and collection bookings can be tracked:",
    ],
    bullets: [
      "Via the Healic Customer App under 'My Orders' or 'My Bookings'",
      "Through SMS updates sent at each milestone",
      "By contacting our support team with your order or booking ID",
    ],
  },
  {
    id: "damaged-missing",
    title: "Damaged or Missing Items",
    content: [
      "If you receive a damaged, incorrect, or incomplete order:",
    ],
    bullets: [
      "Report the issue within 24 hours of delivery via support@healic.in or our helpline",
      "Provide photos of damaged packaging/products and your order ID",
      "We will arrange a replacement or refund after verification, typically within 3-5 business days",
      "For temperature-sensitive medicines that arrive warm or compromised, do not use and report immediately",
    ],
  },
  {
    id: "service-areas",
    title: "Current Service Areas",
    content: [
      "Healic currently offers delivery and home collection services in the following cities and surrounding areas:",
    ],
    bullets: [
      "Gurugram (including Sohna Road)",
      "Agra",
      "Haridwar",
      "Indirapuram, Ghaziabad",
    ],
    afterBullets: [
      "We are continuously expanding our service network. If your area is not currently covered, you may still access our services at our hospital locations.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    content: [
      "For shipping-related queries or to report delivery issues:",
    ],
    bullets: [
      "Email: support@healic.in",
      "Phone: 92669 80080",
      "Working Hours: Monday to Sunday, 7 AM to 12 PM",
    ],
  },
];

function ShippingPolicyPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-accent" />
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Policy
            </p>
          </div>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
            Shipping Policy
          </h1>
          <p className="mt-3 max-w-3xl text-sm opacity-85 sm:text-base">
            Information about delivery timelines, shipping charges, home sample
            collection, and our current service areas.
          </p>
          <p className="mt-4 text-xs opacity-70">
            Last Updated: June 13, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-4 py-10 lg:px-8 lg:py-14">
        <div className="space-y-10">
          {sections.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-soft lg:p-8"
            >
              <h2 className="font-display text-xl font-bold sm:text-2xl">
                {s.title}
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                {s.content.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-foreground/85">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
              {s.afterBullets && (
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                  {s.afterBullets.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
